import { useState } from "react";
import { Box, Card, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import { useQuery } from "react-query";
import { issbKeys } from "../../../../hooks/query/issb";
import { showErrorToast } from "../../../../utils";
import { QCardBody } from "../../IssbDataInput/components/QuestionCard";
import questionService from "../../../../api/issb/question.service";

/**
 * One requirement that uses a figure, expandable to its full answer — the card the metric overlay's
 * "Where it's used" shows, for the "Manage changed data" answers page.
 *
 * The answer is fetched only once the card is opened, with the same per-question GET and query key the metric
 * overlay uses, so a question opened in one is cached for the other. Rendered through `QCardBody readOnly`
 * (the Requirements page's own expanded card) so the preview is the real answer with its inline citations,
 * and nothing in it can change the question's assignees or status. `Card` wraps it unstyled because
 * QCardBody renders CardBody, which throws outside a Card's styles context.
 *
 * `header` is what sits above the question text (reference, stage, version marker); `footer` sits under it
 * whether or not the card is open (e.g. the datapoints it uses).
 */
export default function AnswerPreviewCard({ questionId, question, header, footer }) {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const { data: questionData, isLoading } = useQuery(
    [...issbKeys.answer(), questionId],
    () => questionService.get(questionId),
    {
      enabled: isOpen && !!questionId,
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    }
  );

  return (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="12px" p={4}>
      <Flex align="flex-start" justify="space-between" gap={3} cursor="pointer" onClick={() => setIsOpen((open) => !open)}>
        <Box minW={0}>
          {header}
          <Text fontSize="14px" fontWeight="700" lineHeight="1.4" color="darkTeal">
            {question}
          </Text>
          {footer}
        </Box>
        <Flex w="28px" h="28px" flexShrink={0} borderRadius="50%" bg="teal" align="center" justify="center">
          <CaretDown size={12} color="white" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </Flex>
      </Flex>
      {isOpen ? (
        <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.200">
          {isLoading ? (
            <Spinner size="sm" />
          ) : questionData && questionData.id === questionId ? (
            <Card variant="unstyled" bg="transparent" sx={ANSWER_PREVIEW_TEXT}>
              <QCardBody q={questionData} readOnly />
            </Card>
          ) : (
            <Text fontSize="sm" color="gray.500">
              Could not load this disclosure.
            </Text>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

/*
  The answer text inside a preview, scaled down to the overlay's own type size. The shared RichTextViewer
  renders at page size (it is the Requirements page's answer), which towers over everything else in an
  overlay — so it is overridden here only, never in the shared editor CSS.
*/
const ANSWER_PREVIEW_TEXT = {
  ".tiptap": { fontSize: "14px", lineHeight: 1.55 },
  ".tiptap p, .tiptap li": { fontSize: "14px" },
  ".tiptap h1": { fontSize: "18px" },
  ".tiptap h2": { fontSize: "16px" },
  ".tiptap h3": { fontSize: "15px" },
};
